import React, { createElement, Fragment, useEffect, useMemo, useRef } from 'react';
import { render } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { useSearchBox, useHits } from 'react-instantsearch-hooks-web';
import { autocomplete, getAlgoliaResults, Render } from '@algolia/autocomplete-js';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { SearchResponse } from '@algolia/client-search';
import { useIsMobile } from '@guildeducationinc/catalog-tools';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import { algoliaClient } from 'src/clients/Algolia';
import { useChatDisplay } from 'src/components/Providers/ChatDisplayProvider';
import { useRecommendations } from 'src/components/Providers/RecommendationsProvider';
import { useSearchContext } from 'src/components/Providers/SearchProvider';
import { NoResultsDropdown } from 'src/components/SearchBar/NoResultsDropdown';
import {
	ProgramsHeader,
	ProgramsItem,
	ProgramsFooter,
} from 'src/components/SearchBar/ProgramsPlugin';
import {
	QuerySuggestionsItem,
	QuerySuggestionsHeader,
} from 'src/components/SearchBar/QuerySuggestionsPlugin';
import { QuickAccess } from 'src/components/SearchBar/QuickAccess';
import {
	RecentSearchesHeader,
	RecentSearchesItem,
} from 'src/components/SearchBar/RecentSearchesPlugin';
import {
	RecommendedProgramsFooter,
	RecommendedProgramsHeader,
} from 'src/components/SearchBar/RecommendedProgramsPlugin';
import { onFocus, onModalClose } from 'src/components/SearchBar/searchBarHelpers';
import { SuggestedSearches } from 'src/components/SearchBar/SuggestedSearches';
import { useAlgoliaStats } from 'src/hooks';
import { trackEvent } from 'src/services/analytics';
import { datadogRum } from '@datadog/browser-rum';
import { getUserValuesForPath } from '@src/constants/getUserValuesForPath';
import { AutocompleteProviders } from '../Providers/AutocompleteProviders';
import styles from './SearchBar.module.scss';

export const SearchBar: React.FC = () => {
	const { page } = useAlgoliaStats();
	const { userFromIdToken: user, getAccessToken, getIdToken } = useMemberExperienceAuth();
	const authUser = getUserValuesForPath(user);
	const { setHideChat } = useChatDisplay();
	const history = useHistory();
	const { results } = useHits();
	const isMobile = useIsMobile();
	const { recommendationsData } = useRecommendations();
	const { query, refine } = useSearchBox();
	const { filters, index, planUuid, staticTotalPrograms } = useSearchContext();

	const autocompleteContainerRef = useRef<HTMLDivElement>(null);

	const recommendations = recommendationsData?.recommendations || [];
	const availableRecommendations =
		recommendations.length > 3 ? recommendations.slice(0, 3) : recommendations;
	const availableRecIds = availableRecommendations.map(rec => rec.uuid);

	const plugins = useMemo(() => {
		const recommendedProgramsPlugin = {
			getSources({ query }) {
				if (query || !recommendationsData?.recommendations?.length) {
					return [];
				}

				return [
					{
						sourceId: 'recommendedProgramsPlugin',
						getItems({ setContext }) {
							return getAlgoliaResults({
								searchClient: algoliaClient,
								queries: [
									{
										indexName: index,
										params: {
											filters: `${filters} AND uuid:${availableRecIds.join(' OR uuid:')}`,
											hitsPerPage: 3,
											userToken: authUser.id,
										},
										query: '',
									},
								],
								transformResponse({ hits, results }) {
									setContext({
										nbPrograms: (results[0] as SearchResponse).nbHits,
									});
									return hits;
								},
							});
						},
						onSelect({ item }) {
							history.push(`/programs/${item.id}`);
						},
						templates: {
							header() {
								return <RecommendedProgramsHeader />;
							},
							item({ item, state }) {
								return (
									<ProgramsItem
										planUuid={planUuid}
										program={item}
										results={results}
										state={state}
										user={authUser}
									/>
								);
							},
							footer() {
								return <RecommendedProgramsFooter recommendations={recommendations} />;
							},
						},
					},
				];
			},
		};

		const programsPlugin = {
			getSources({ query }) {
				if (!query) {
					return [];
				}

				return [
					{
						sourceId: 'programsPlugin',
						getItems({ setContext }) {
							return getAlgoliaResults({
								searchClient: algoliaClient,
								queries: [
									{
										indexName: index,
										params: {
											filters,
											hitsPerPage: 3,
											userToken: authUser.id,
										},
										query,
									},
								],
								transformResponse({ hits, results }) {
									setContext({
										nbPrograms: (results[0] as SearchResponse).nbHits,
									});
									return hits;
								},
							});
						},
						onSelect({ item }) {
							history.push({
								pathname: `/programs/${item.id}/?queryID=${results?._rawResults[0].queryID}`,
								state: { fromGuildCatalog: true },
							});
						},
						templates: {
							header({ state }) {
								return <ProgramsHeader state={state} />;
							},
							item({ item, state }) {
								return (
									<ProgramsItem
										planUuid={planUuid}
										program={item}
										results={results}
										state={state}
										user={authUser}
									/>
								);
							},
							footer({ state }) {
								return <ProgramsFooter page={page} refine={refine} state={state} />;
							},
						},
					},
				];
			},
		};

		const querySuggestionsPlugin = createQuerySuggestionsPlugin({
			searchClient: algoliaClient,
			indexName: `${index}_query_suggestions`,
			getSearchParams() {
				return {
					hitsPerPage: 5,
					userToken: authUser.id,
				};
			},
			transformSource({ source, state }) {
				return {
					...source,
					sourceId: 'querySuggestionsPlugin',
					onSelect({ item }) {
						refine(item.query);
						trackEvent('Search', {
							searchState: {
								pageNumber: page + 1,
								sourceId: state.query ? source.sourceId : 'popularSearchSuggestion',
							},
							typedTerm: item.query,
						});
						onModalClose();
					},
					templates: {
						header() {
							return <QuerySuggestionsHeader query={state.query} />;
						},
						item({ item }) {
							return <QuerySuggestionsItem item={item} query={state.query} />;
						},
					},
				};
			},
		});

		const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
			key: 'recentSearches',
			limit: 5,
			transformSource({ source, onRemove }) {
				return {
					...source,
					onSelect({ item }) {
						refine(item.label);
						trackEvent('Search', {
							searchState: {
								pageNumber: page + 1,
								sourceId: source.sourceId,
							},
							typedTerm: item.label,
						});
						onModalClose();
					},
					templates: {
						header() {
							return <RecentSearchesHeader />;
						},
						item({ item }) {
							return <RecentSearchesItem item={item} onRemove={onRemove} refine={refine} />;
						},
					},
				};
			},
		});

		return [
			programsPlugin,
			querySuggestionsPlugin,
			recentSearchesPlugin,
			recommendedProgramsPlugin,
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [history, page, planUuid, recommendationsData, refine, results, authUser.id]);

	useEffect(() => {
		if (!autocompleteContainerRef.current) {
			return;
		}

		const autocompleteInstance = autocomplete({
			container: autocompleteContainerRef.current,
			detachedMediaQuery: isMobile ? '' : 'none',
			placeholder: '',
			initialState: { query },
			openOnFocus: true,
			insights: true,
			onReset() {
				refine('');
				onModalClose();
			},
			onSubmit({ state }) {
				refine(state.query);
				trackEvent('Search', {
					searchState: {
						pageNumber: page + 1,
					},
					typedTerm: state.query,
				});
				onModalClose();
			},
			renderNoResults({ render }, root) {
				render(
					<AutocompleteProviders
						authUser={user}
						getAccessToken={getAccessToken}
						getIdToken={getIdToken}
					>
						<NoResultsDropdown />
					</AutocompleteProviders>,
					root
				);
			},
			plugins,
			renderer: {
				createElement,
				Fragment,
				render: render as unknown as Render,
			},
			render({ elements, state }, root) {
				const {
					programsPlugin,
					querySuggestionsPlugin,
					recentSearchesPlugin,
					recommendedProgramsPlugin,
				} = elements;
				const showNoResultsDropdown = state.query !== '' && !programsPlugin;

				if (state.status === 'error') {
					datadogRum.addAction('AlgoliaAutocompleteError', {
						planUuid,
					});
					return () => autocompleteInstance.refresh();
				}

				const programUuids = state.collections
					.flatMap(collection => collection.items)
					.filter(item => Object.prototype.hasOwnProperty.call(item, 'uuid'))
					.map(item => item.uuid)
					.filter((uuid): uuid is string => uuid !== undefined);

				render(
					<AutocompleteProviders
						authUser={user}
						getAccessToken={getAccessToken}
						getIdToken={getIdToken}
						programUuids={[...programUuids, ...availableRecIds]}
					>
						<div className='aa-PanelLayout'>
							{showNoResultsDropdown ? (
								<NoResultsDropdown />
							) : (
								<div className={styles.aaPanelSections}>
									<SuggestedSearches
										querySuggestionsPlugin={querySuggestionsPlugin}
										recentSearchesPlugin={recentSearchesPlugin}
										state={state}
									/>
									<QuickAccess
										programsPlugin={programsPlugin}
										recommendedProgramsPlugin={recommendedProgramsPlugin}
										staticTotalPrograms={staticTotalPrograms}
									/>
								</div>
							)}
						</div>
					</AutocompleteProviders>,
					root
				);
			},
		});

		return () => autocompleteInstance.destroy();
	}, [
		user,
		autocompleteContainerRef,
		availableRecIds,
		getAccessToken,
		getIdToken,
		isMobile,
		page,
		planUuid,
		plugins,
		query,
		refine,
		results,
		setHideChat,
		staticTotalPrograms,
	]);

	return (
		<div
			className={styles.searchBarContainer}
			data-testid='search-input'
			onFocus={() => onFocus(page, query)}
			ref={autocompleteContainerRef}
		/>
	);
};
